import { Controller } from "@hotwired/stimulus";
import StimulusReflex from "stimulus_reflex";

export default class extends Controller {
  static targets = [
    "input",
    "results",
    "selectedId",
    "vintageResults",
    "vintageInput",
    "selectedVintageDisplay",
    "vintageInputContainer",
    "selectedVintageText",
    "vintagePreviewContainer",
    "bottleUploadContainer",
    "wineAttributesContainer",
    "varietiesContainer",
    "spinner",
  ];
  static values = {
    organizationId: String,
    selectedWineId: String,
  };

  connect() {
    console.log("WineSearchController connected");
    console.log("Element:", this.element);
    console.log("Input target:", this.inputTarget);
    console.log("Results target:", this.resultsTarget);
    console.log("Selected ID target:", this.selectedIdTarget);
    console.log("Initial organization ID:", this.organizationIdValue);

    StimulusReflex.register(this);
    this.restoreSelectedWineState();

    // Store bound methods for proper event listener removal
    this.boundHandleOrganizationSelected =
      this.handleOrganizationSelected.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleChange = this.handleChange.bind(this);

    // Listen for organization selection from organization search
    document.addEventListener(
      "organization:selected",
      this.boundHandleOrganizationSelected
    );

    // Add direct event listener to input for debugging
    this.inputTarget.addEventListener("input", (e) => {
      console.log("Direct input event:", e.target.value);
      this.search();

      // Only handle new wine input for public wines (organization wines have sections visible by default)
      const context = this.element.dataset.context || "public";
      if (context === "public") {
        this.handleNewWineInput();
      }
    });

    // Add keydown listener to handle clearing wine selection
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown);

    // Add change listener to handle manual clearing of input
    this.inputTarget.addEventListener("change", this.boundHandleChange);

    // Add focus/blur listeners for spinner control
    this.inputTarget.addEventListener("focus", this.handleFocus.bind(this));
    this.inputTarget.addEventListener("blur", this.handleBlur.bind(this));

    // Add click outside listener
    document.addEventListener("click", this.clickOutside.bind(this));

    // Check if we already have an organization ID from the data attribute
    const initialOrgId = this.element.dataset.organizationId;
    if (initialOrgId) {
      console.log("Found initial organization ID:", initialOrgId);
      this.organizationIdValue = initialOrgId;
    }
  }

  disconnect() {
    document.removeEventListener(
      "organization:selected",
      this.boundHandleOrganizationSelected
    );
    document.removeEventListener("click", this.clickOutside.bind(this));
  }

  handleOrganizationSelected(event) {
    const organizationId = event.detail.organizationId;
    console.log("Organization selected event received:", {
      organizationId,
      detail: event.detail,
    });

    if (!organizationId) {
      console.warn(
        "Organization selected event received but organizationId is empty"
      );
      return;
    }

    this.organizationIdValue = organizationId;
    // Clear wine selection when organization changes
    this.selectedIdTarget.value = "";
    this.inputTarget.value = "";
    this.selectedWineIdValue = "";
    this.hideVintageResults();

    // Update the organization-id attribute
    this.element.setAttribute("data-organization-id", organizationId);

    console.log("Wine search state after organization selection:", {
      organizationId: this.organizationIdValue,
      "dataset-org-id": this.element.getAttribute("data-organization-id"),
      selectedId: this.selectedIdTarget.value,
      inputValue: this.inputTarget.value,
    });
  }

  search() {
    console.log("Search method called");
    const query = this.inputTarget.value.trim();
    console.log("Search triggered with query:", query);
    console.log("Organization ID:", this.organizationIdValue);
    console.log("Element dataset before:", this.element.dataset);

    if (!query || !this.organizationIdValue) {
      console.log("Search aborted - missing query or organization ID");
      this.resultsTarget.classList.add("hidden");
      this.hideSpinner();
      return;
    }

    // Show spinner and results container before search
    this.showSpinner();
    this.resultsTarget.classList.remove("hidden");

    // Set the query, organization-id, and context attributes
    this.element.setAttribute("data-query", query);
    this.element.setAttribute("data-organization-id", this.organizationIdValue);

    // Get context from element dataset (defaults to "public" if not set)
    const context = this.element.dataset.context || "public";
    this.element.setAttribute("data-context", context);

    console.log("Dataset after setting:", {
      query: this.element.getAttribute("data-query"),
      "organization-id": this.element.getAttribute("data-organization-id"),
      context: this.element.getAttribute("data-context"),
    });

    try {
      console.log("Attempting to trigger WineSearch#search reflex");
      this.stimulate("WineSearch#search");
      console.log("Reflex triggered successfully");
    } catch (error) {
      console.error("Error triggering reflex:", error);
      this.resultsTarget.classList.add("hidden");
      this.hideSpinner();
    }
  }

  // Handle when user is typing a new wine name (not selecting from search results)
  handleNewWineInput() {
    const query = this.inputTarget.value.trim();
    const context = this.element.dataset.context || "public";

    // If user is typing and no wine is selected, show wine attributes and varieties for new wine creation
    if (query && !this.selectedIdTarget.value) {
      if (context === "organization") {
        // For organization wine entries: always show varieties for new wine creation
        if (this.hasVarietiesContainerTarget) {
          this.varietiesContainerTarget.classList.remove("hidden");
        }

        // Show wine attributes container for new wine
        if (this.hasWineAttributesContainerTarget) {
          this.wineAttributesContainerTarget.classList.remove("hidden");
        }
      } else {
        // For public wines: show wine attributes and varieties for new wine creation
        if (this.hasWineAttributesContainerTarget) {
          this.wineAttributesContainerTarget.classList.remove("hidden");
        }

        if (this.hasVarietiesContainerTarget) {
          this.varietiesContainerTarget.classList.remove("hidden");
        }
      }

      // Hide vintage results since we're creating a new wine
      this.vintageResultsTarget.classList.add("hidden");
      this.selectedVintageDisplayTarget.classList.add("hidden");
      this.vintagePreviewContainerTarget.classList.add("hidden");
    }
  }

  // Handle when wine selection is cleared
  clearWineSelection() {
    const context = this.element.dataset.context || "public";

    // Clear the selected wine
    this.selectedIdTarget.value = "";
    this.selectedWineIdValue = "";

    if (context === "organization") {
      // For organization wine entries: show wine attributes for new wine creation when selection is cleared
      if (this.hasWineAttributesContainerTarget) {
        this.wineAttributesContainerTarget.classList.remove("hidden");
      }

      // Varieties section is already visible by default for organization wine entries
    } else {
      // For public wines: show wine attributes and varieties for new wine creation
      if (this.hasWineAttributesContainerTarget) {
        this.wineAttributesContainerTarget.classList.remove("hidden");
      }

      if (this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.remove("hidden");
      }
    }

    // Hide vintage-related containers
    this.vintageResultsTarget.classList.add("hidden");
    this.selectedVintageDisplayTarget.classList.add("hidden");
    this.vintagePreviewContainerTarget.classList.add("hidden");
    this.vintageInputContainerTarget.classList.add("hidden");
  }

  // Handle keydown events
  handleKeydown(e) {
    // If user is backspacing/deleting and the input becomes empty, clear wine selection
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      this.inputTarget.value.length <= 1
    ) {
      this.clearWineSelection();
    }
  }

  // Handle change events
  handleChange(e) {
    if (!e.target.value.trim()) {
      this.clearWineSelection();
    }
  }

  // Reflex lifecycle methods
  beforeSearch() {
    console.log("beforeSearch lifecycle method called");
    // Don't add hidden class here since we already show it in search()
    this.showSpinner();
  }

  searchSuccess() {
    console.log("searchSuccess lifecycle method called");
    this.hideSpinner();
    // Only hide if no results
    if (this.resultsTarget.querySelectorAll("[data-wine-id]").length === 0) {
      this.resultsTarget.classList.add("hidden");
      // If no results found and user is typing, show wine attributes and varieties for new wine creation
      const query = this.inputTarget.value.trim();
      const context = this.element.dataset.context || "public";

      if (query && !this.selectedIdTarget.value) {
        if (context === "public") {
          // For public wines: show wine attributes and varieties for new wine creation
          if (this.hasWineAttributesContainerTarget) {
            this.wineAttributesContainerTarget.classList.remove("hidden");
          }

          if (this.hasVarietiesContainerTarget) {
            this.varietiesContainerTarget.classList.remove("hidden");
          }
        }
        // For organization wine entries: sections are already visible by default, no need to toggle
      }
    }
  }

  searchError(error) {
    console.error("searchError lifecycle method called", error);
    this.resultsTarget.classList.add("hidden");
    this.hideSpinner();
  }

  selectWine(event) {
    console.log("Wine selected:", event.currentTarget.dataset);
    const wineId = event.currentTarget.dataset.wineId;
    const wineName = event.currentTarget.dataset.wineName;
    const context = this.element.dataset.context || "public";

    this.selectedIdTarget.value = wineId;
    this.inputTarget.value = wineName;
    this.selectedWineIdValue = wineId;
    this.resultsTarget.classList.add("hidden");
    this.hideSpinner();

    if (context === "organization") {
      // For organization wine entries: allow wine selection but not vintage selection
      // Always show varieties for new vintage creation
      if (this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.remove("hidden");
      }

      // Hide wine attributes since we're using an existing wine
      if (this.hasWineAttributesContainerTarget) {
        this.wineAttributesContainerTarget.classList.add("hidden");
      }

      // Don't show vintage selection - user will create a new vintage
      // Keep vintage input visible for manual entry
      this.vintageResultsTarget.classList.add("hidden");
      this.selectedVintageDisplayTarget.classList.add("hidden");
      this.vintagePreviewContainerTarget.classList.add("hidden");
    } else {
      // For public wines: allow both wine and vintage selection
      // Hide wine attributes and varieties when a wine is selected
      if (this.hasWineAttributesContainerTarget) {
        this.wineAttributesContainerTarget.classList.add("hidden");
      }
      if (this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.add("hidden");
      }

      // Show vintage selection
      this.showVintageSelection();
    }
  }

  showVintageSelection() {
    const context = this.element.dataset.context || "public";

    // For organization wine entries, don't show vintage selection
    if (context === "organization") {
      console.log("Vintage selection not shown in organization context");
      return;
    }

    if (!this.selectedWineIdValue) return;

    // Show vintage results container before loading
    this.vintageResultsTarget.classList.remove("hidden");
    this.vintageResultsTarget.classList.add("opacity-50");

    // Set the wine ID and context in the dataset for the reflex to access
    this.element.dataset.wineId = this.selectedWineIdValue;
    this.element.setAttribute("data-context", context);

    try {
      console.log("Attempting to trigger WineSearch#load_vintages reflex");
      console.log("Dataset before reflex:", this.element.dataset);
      this.stimulate("WineSearch#load_vintages");
      console.log("Vintages reflex triggered successfully");
    } catch (error) {
      console.error("Error triggering vintages reflex:", error);
      this.vintageResultsTarget.classList.add("hidden");
      this.vintageResultsTarget.classList.remove("opacity-50");
    }
  }

  // Vintage selection methods
  beforeLoad_vintages() {
    console.log("beforeLoad_vintages lifecycle method called");
    // Don't add hidden class here since we already show it in showVintageSelection()
    this.vintageResultsTarget.classList.add("opacity-50");
  }

  load_vintagesSuccess() {
    console.log("load_vintagesSuccess lifecycle method called");
    this.vintageResultsTarget.classList.remove("opacity-50");
    // Only hide if no results
    if (
      this.vintageResultsTarget.querySelectorAll(
        "[data-wine-search-vintage-id-value]"
      ).length === 0
    ) {
      this.vintageResultsTarget.classList.add("hidden");
    }
  }

  load_vintagesError(error) {
    console.error("load_vintagesError lifecycle method called", error);
    this.vintageResultsTarget.classList.add("hidden");
    this.vintageResultsTarget.classList.remove("opacity-50");
  }

  selectVintage(event) {
    const context = this.element.dataset.context || "public";

    // For organization wine entries, don't allow vintage selection
    if (context === "organization") {
      console.log("Vintage selection not allowed in organization context");
      return;
    }

    const vintageId = event.currentTarget.dataset.vintageId;
    const vintageYear = event.currentTarget.dataset.vintageYear;

    // Hide vintage results
    this.vintageResultsTarget.classList.add("hidden");

    // Show selected vintage display
    this.selectedVintageDisplayTarget.classList.remove("hidden");
    this.selectedVintageTextTarget.textContent = vintageYear;

    // Hide vintage input container
    this.vintageInputContainerTarget.classList.add("hidden");

    // Hide varieties container since we're using an existing vintage
    if (this.hasVarietiesContainerTarget) {
      this.varietiesContainerTarget.classList.add("hidden");
    }

    // Show preview
    this.showVintagePreview(this.selectedWineIdValue, vintageId);
  }

  hideVintageResults() {
    this.vintageResultsTarget.classList.add("hidden");
  }

  // Hide results when clicking outside
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.resultsTarget.classList.add("hidden");
      this.vintageResultsTarget.classList.add("hidden");

      // If no wine is selected but user has typed something, show wine attributes and varieties
      const query = this.inputTarget.value.trim();
      const context = this.element.dataset.context || "public";

      if (query && !this.selectedIdTarget.value) {
        if (context === "public") {
          // For public wines: show wine attributes and varieties for new wine creation
          if (this.hasWineAttributesContainerTarget) {
            this.wineAttributesContainerTarget.classList.remove("hidden");
          }

          if (this.hasVarietiesContainerTarget) {
            this.varietiesContainerTarget.classList.remove("hidden");
          }
        }
        // For organization wine entries: sections are already visible by default, no need to toggle
      }
    }
  }

  createNewVintage() {
    const context = this.element.dataset.context || "public";

    // Hide vintage results
    this.vintageResultsTarget.classList.add("hidden");

    // Show vintage input container
    this.vintageInputContainerTarget.classList.remove("hidden");

    if (context === "organization") {
      // For organization wine entries: always show varieties for new vintage creation
      if (this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.remove("hidden");
      }
    } else {
      // For public wines: show varieties container for new vintage
      if (this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.remove("hidden");
      }
    }

    // Hide selected vintage display
    this.selectedVintageDisplayTarget.classList.add("hidden");

    // Hide preview
    this.vintagePreviewContainerTarget.classList.add("hidden");
  }

  showVintagePreview(wineId, vintageId) {
    // Use StimulusReflex to morph the preview container with the selected vintage's details
    this.vintagePreviewContainerTarget.classList.remove("hidden");
    this.bottleUploadContainerTarget.classList.add("hidden");

    // Set the context in the dataset for the reflex to access
    const context = this.element.dataset.context || "public";
    this.element.setAttribute("data-context", context);

    // Trigger the reflex to fetch and morph the preview
    this.stimulate("WineSearch#preview_vintage", wineId, vintageId);
  }

  restoreSelectedWineState() {
    // Restore selected wine state after reload
    if (this.hasSelectedIdTarget && this.selectedIdTarget.value) {
      const context = this.element.dataset.context || "public";

      // If a wine is selected, show it in the input and hide attributes
      if (this.hasWineAttributesContainerTarget) {
        this.wineAttributesContainerTarget.classList.add("hidden");
      }

      // Only hide varieties in public context, not organization context
      if (context === "public" && this.hasVarietiesContainerTarget) {
        this.varietiesContainerTarget.classList.add("hidden");
      }

      this.resultsTarget.classList.add("hidden");
      // Optionally, you could show a summary UI here
    }
  }

  createNewWine(event) {
    if (event) {
      event.preventDefault();
    }
    this.resultsTarget.classList.add("hidden");
    this.inputTarget.focus();
    // Optionally, you might want to clear the input or do other cleanup
    // this.inputTarget.value = '';
    // this.selectedIdTarget.value = '';
  }

  handleFocus() {
    // Show spinner when input is focused and has content
    if (this.inputTarget.value.trim()) {
      this.showSpinner();
    }
  }

  handleBlur() {
    // Hide spinner when input loses focus
    setTimeout(() => {
      this.hideSpinner();
    }, 200); // Small delay to allow for click events on results
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  editVintageYear(event) {
    event.preventDefault();
    console.log("Edit vintage year clicked");

    // Hide selected vintage display
    this.selectedVintageDisplayTarget.classList.add("hidden");

    // Show vintage input container
    this.vintageInputContainerTarget.classList.remove("hidden");

    // Focus on vintage input
    if (this.hasVintageInputTarget) {
      this.vintageInputTarget.focus();
    }

    // Show varieties container for editing
    if (this.hasVarietiesContainerTarget) {
      this.varietiesContainerTarget.classList.remove("hidden");
    }

    // Hide preview
    this.vintagePreviewContainerTarget.classList.add("hidden");
  }
}
